import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { TypographyUpdateEvent, TypographyConfig, TypographyState } from './typography.interface';

@Injectable({
  providedIn: 'root'
})
export class TypographyEventBus {
  private typographyEventSubject = new Subject<TypographyUpdateEvent>();

  /**
   * Emit typography update event
   */
  emitTypographyUpdate(config: TypographyConfig): void {
    const event: TypographyUpdateEvent = {
      type: 'TYPOGRAPHY_UPDATE',
      payload: config,
      timestamp: new Date()
    };
    
    console.log('📡 TypographyEventBus: Emitting typography update event:', event);
    this.typographyEventSubject.next(event);
  }

  /**
   * Emit typography loaded event
   */
  emitTypographyLoaded(state: TypographyState): void {
    const event: TypographyUpdateEvent = {
      type: 'TYPOGRAPHY_LOADED',
      payload: state,
      timestamp: new Date()
    };
    
    console.log('📡 TypographyEventBus: Emitting typography loaded event:', event);
    this.typographyEventSubject.next(event);
  }

  /**
   * Emit typography error event
   */
  emitTypographyError(error: string, fallbackState?: TypographyState): void {
    const event: TypographyUpdateEvent = {
      type: 'TYPOGRAPHY_ERROR',
      payload: error,
      timestamp: new Date()
    };
    
    console.log('📡 TypographyEventBus: Emitting typography error event:', event);
    this.typographyEventSubject.next(event);
  }

  /**
   * Emit typography saved event
   */
  emitTypographySaved(config: TypographyConfig): void {
    const event: TypographyUpdateEvent = {
      type: 'TYPOGRAPHY_SAVED',
      payload: config,
      timestamp: new Date()
    };
    
    console.log('📡 TypographyEventBus: Emitting typography saved event:', event);
    this.typographyEventSubject.next(event);
  }

  /**
   * Listen for typography events
   */
  onTypographyEvent(): Observable<TypographyUpdateEvent> {
    return this.typographyEventSubject.asObservable();
  }

  /**
   * Listen for specific typography event types
   */
  onTypographyUpdate(): Observable<TypographyConfig> {
    return this.typographyEventSubject.pipe(
      filter(event => event.type === 'TYPOGRAPHY_UPDATE'),
      map(event => event.payload as TypographyConfig)
    );
  }

  onTypographyLoaded(): Observable<TypographyState> {
    return this.typographyEventSubject.pipe(
      filter(event => event.type === 'TYPOGRAPHY_LOADED'),
      map(event => event.payload as TypographyState)
    );
  }

  onTypographyError(): Observable<string> {
    return this.typographyEventSubject.pipe(
      filter(event => event.type === 'TYPOGRAPHY_ERROR'),
      map(event => event.payload as string)
    );
  }

  onTypographySaved(): Observable<TypographyConfig> {
    return this.typographyEventSubject.pipe(
      filter(event => event.type === 'TYPOGRAPHY_SAVED'),
      map(event => event.payload as TypographyConfig)
    );
  }

  /**
   * Clear all event listeners
   */
  clear(): void {
    console.log('📡 TypographyEventBus: Clearing all event listeners');
    this.typographyEventSubject.complete();
  }
}

// Import RxJS operators
import { filter, map } from 'rxjs/operators';
